// Global Instructions Rule Applied!
//
// The review-status chip a rendered report carries about its period
// highlights (owner instruction 2026-09-07): any user running a report sees
// at once whether the highlights were reviewed and signed off by a person,
// only generated and approved by policy, edited after the sign-off, or
// absent — and the tooltip says where and how to review. Tag vocabulary:
// green = signed off, gold = needs attention, default = none.
import React from 'react'
import { Tag, Tooltip } from 'antd'
import dayjs from 'dayjs'
import type { ReportHighlightsStatus } from '../../../state_manager/data_models/period_highlights_model.ts'

const WHERE = 'Reporting → Period Highlights'

export const HighlightsReviewStatus: React.FC<{ status: ReportHighlightsStatus }> = ({ status }) => {
  const where = `${WHERE}${status.scope_name ? ` → ${status.scope_name}${status.period_label ? ` · ${status.period_label}` : ''}` : ''}`
  const signed = status.signed_off_by
    ? `Signed off by ${status.signed_off_by}${status.signed_off_at ? ` on ${dayjs(status.signed_off_at).format('MM/DD/YYYY')}` : ''}`
    : 'Signed off'
  switch (status.status) {
    case 'signed_off':
      return (
        <Tooltip title={`${signed}${status.version != null ? ` (v${status.version})` : ''}. The narrative and stories in this report were read and approved by a person.`}>
          <Tag color='green'>Highlights signed off</Tag>
        </Tooltip>
      )
    case 'edited_since_sign_off':
      return (
        <Tooltip title={`${signed}, but the set was edited afterwards. Re-read it on ${where} and press Sign off again.`}>
          <Tag color='gold'>Highlights edited since sign-off</Tag>
        </Tooltip>
      )
    case 'unreviewed':
      return (
        <Tooltip title={`The highlights in this report were generated by the agent and approved by policy — nobody has reviewed them yet. Open ${where}, read the narrative and the stories, edit them in place if needed, then press Sign off.`}>
          <Tag color='gold'>Highlights not reviewed</Tag>
        </Tooltip>
      )
    default:
      return (
        <Tooltip title={`No period highlights exist for this report's period yet. Generate a set on ${WHERE}; once it lands it can be reviewed and signed off there.`}>
          <Tag>No highlights</Tag>
        </Tooltip>
      )
  }
}

export default HighlightsReviewStatus
